import { ChangeDetectionStrategy, Component, Input } from '@angular/core'
import { CommonModule } from '@angular/common'
import { FormControl, FormGroup, ReactiveFormsModule } from '@angular/forms'
import { MatCheckboxModule } from '@angular/material/checkbox'
import { MatFormFieldModule } from '@angular/material/form-field'
import { MatInputModule } from '@angular/material/input'
import { MatSelectModule } from '@angular/material/select'
import { MatTooltipModule } from '@angular/material/tooltip'
import { TranslateModule } from '@ngx-translate/core'
import { GbDashboardSharedUiModule } from 'libs/gb-dashboard/shared/ui/src/lib/gb-dashboard-shared-ui.module'
import { MatButtonToggleModule } from '@angular/material/button-toggle'
import { SharedUiModule } from 'libs/shared/ui/src/lib/shared-ui.module'
import { GbDashboardSharedUtilsModule } from 'libs/gb-dashboard/shared/utils/src/lib/gb-dashboard-shared-utils.module'
import { GbPresenterToolsTextComponent } from '../gb-presenter-tools-text/gb-presenter-tools-text.component'
import { GbPresenterToolsAdvancedOptionsContainerComponent } from '../gb-presenter-tools-advanced-options-container/gb-presenter-tools-advanced-options-container.component'

@Component({
  selector: 'gb-presenter-tools-countdown',
  imports: [
    MatButtonToggleModule,
    MatTooltipModule,
    CommonModule,
    ReactiveFormsModule,
    MatFormFieldModule,
    MatInputModule,
    MatSelectModule,
    MatCheckboxModule,
    TranslateModule,
    GbDashboardSharedUiModule,
    SharedUiModule,
    GbDashboardSharedUtilsModule,
    GbPresenterToolsTextComponent,
    GbPresenterToolsAdvancedOptionsContainerComponent,
  ],
  templateUrl: './gb-presenter-tools-countdown.component.html',
  styleUrl: './gb-presenter-tools-countdown.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class GbPresenterToolsCountdownComponent {
  private toolsFormGroupInternal: FormGroup | null = null
  private auctionScheduledEndInternal: string | null = null
  private auctionCloseResetTimeout: ReturnType<typeof setTimeout> | null = null

  @Input() set toolsFormGroup(value: FormGroup) {
    this.toolsFormGroupInternal = value
    this.ensureAuctionCloseToggleAvailability()
  }

  get toolsFormGroup(): FormGroup {
    return this.toolsFormGroupInternal as FormGroup
  }

  @Input() set auctionScheduledEnd(value: string | null) {
    this.auctionScheduledEndInternal = value
    this.ensureAuctionCloseToggleAvailability()
  }

  get auctionScheduledEnd(): string | null {
    return this.auctionScheduledEndInternal
  }
  @Input() auctionTimezone = 'UTC'
  @Input() auctionOvertimeEnabled: boolean | null = null
  @Input() allowPastEndDate = false

  hideFields = ['font_style']

  get endControl(): FormControl {
    return this.toolsFormGroup.get('end') as FormControl
  }

  get timeoutTextControl(): FormControl {
    return this.toolsFormGroup.get('timeout_text') as FormControl
  }

  get endOnAuctionScheduleCloseControl(): FormControl {
    return this.toolsFormGroup.get('end_on_auction_schedule_close') as FormControl
  }

  get timerTypeControl(): FormControl {
    return this.toolsFormGroup.get('timer_type') as FormControl
  }

  get plusOvertimeLabelControl(): FormControl {
    return this.toolsFormGroup.get('plus_overtime_label') as FormControl
  }

  get letterSpacingControl(): FormControl {
    return this.toolsFormGroup.get('letter_spacing') as FormControl
  }

  onEndOnAuctionScheduleCloseToggleChange(value: string): void {
    if (value === 'auction_close' && !this.auctionScheduledEnd) {
      this.endOnAuctionScheduleCloseControl.setValue(false)
      return
    }

    const useAuctionClose = value === 'auction_close'
    this.endOnAuctionScheduleCloseControl.setValue(useAuctionClose)

    if (!useAuctionClose && !this.endControl.value) {
      this.endControl.markAsTouched()
      this.endControl.updateValueAndValidity()
    }
  }

  private ensureAuctionCloseToggleAvailability(): void {
    if (!this.toolsFormGroupInternal) return

    if (this.auctionCloseResetTimeout) {
      clearTimeout(this.auctionCloseResetTimeout)
      this.auctionCloseResetTimeout = null
    }

    if (!this.auctionScheduledEndInternal && this.endOnAuctionScheduleCloseControl.value) {
      this.auctionCloseResetTimeout = setTimeout(() => {
        this.auctionCloseResetTimeout = null
        if (!this.toolsFormGroupInternal) return
        if (!this.auctionScheduledEndInternal && this.endOnAuctionScheduleCloseControl.value) {
          this.endOnAuctionScheduleCloseControl.setValue(false)
        }
      }, 100)
    }
  }
}
